import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  RemoveAlertDialog,
  useDisclosure,
} from '@wegroup/design-system';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { v4, validate } from 'uuid';
import { isNil, omitBy } from 'lodash';
import { format, formatISO, isValid } from 'date-fns';
import { Option } from '../../../../../../../design-system/src/content/components/SearchPickerInput';
import useInsurancePolicyModal, {
  Page,
} from '../../../../../../../advisory-report/src/features/creation-flow/components/insurance-policies/hooks/useInsurancePolicyModal';
import {
  Coverage,
  InsurancePolicy,
  InsurancePolicyType,
} from '../../../../../../../advisory-report/src/types/AdvisoryReport';
import {
  InsuranceDetailFormValues,
  InsuranceDetailSchema,
} from '../../../../../../../advisory-report/src/features/creation-flow/types/Form';
import AddInsuranceModalContextProvider from '../../../../../../../advisory-report/src/features/creation-flow/components/insurance-policies/shared/modal/AddInsuranceModalContextProvider';
import AddInsuranceModalBody from '../../../../../../../advisory-report/src/features/creation-flow/components/insurance-policies/shared/modal/AddInsuranceModalBody';
import AddInsuranceModalFooter from '../../../../../../../advisory-report/src/features/creation-flow/components/insurance-policies/shared/modal/AddInsuranceModalFooter';
import { useUpdateInsurancePolicy } from '../../../api/insurance-policies/putInsurancePoliciesForParty';
import { PartyType } from '../../../../../../../../types/SMT';

interface Props {
  defaultIsOpenCoverage?: boolean;
  isOpen: boolean;
  initialPage?: Page;
  initialData?: InsurancePolicy;
  onClose: () => void;
  mode: 'ADD' | 'EDIT';
  type: InsurancePolicyType;
  customRiskObjects?: Option[];
  partyId: string;
  partyType?: PartyType;
}

const defaultValues = {
  name: '',
  deductible: '',
  premium: undefined,
  insured_value: undefined,
  closed_at_distribution: true,
  due_date: undefined,
  start_date: undefined,
  policy_nr: '',
  description: '',
  documents: undefined,
  insurance_company: '',
  payment_interval: 'ANNUAL',
};

const PartyAddInsurancePolicyModal: React.FC<Props> = ({
  onClose,
  isOpen,
  mode,
  defaultIsOpenCoverage,
  initialPage,
  customRiskObjects = [],
  initialData,
  type,
  partyId,
  partyType,
}) => {
  const getDefaultValues = () => {
    // Omit data to remove all undefined or null values from the object
    // This will prevent form errors when a number or string is not nullable
    const omittedData = omitBy(initialData, isNil);
    return {
      ...defaultValues,
      ...omittedData,
      insurance_company: omittedData.coverages?.[0]?.insurance_company || '',
      due_date:
        omittedData.due_date && isValid(omittedData.due_date)
          ? formatISO(new Date(omittedData.due_date))
          : undefined,
      coverages:
        omittedData.coverages?.map(
          ({
            description,
            risk_object,
            risk_object_id,
            ...other
          }: Coverage) => ({
            description: description || '',
            premium: undefined,
            insured_value: undefined,
            risk_object: risk_object || t('OTHER'),
            risk_object_id: risk_object_id || 'OTHER',
            ...omitBy(other, isNil),
          }),
        ) || [],
    };
  };
  const { t } = useTranslation();
  const closeAlert = useDisclosure();
  const updateOrAddPolicy = useUpdateInsurancePolicy();
  const methods = useForm<InsuranceDetailFormValues>({
    defaultValues: getDefaultValues(),
    resolver: yupResolver(InsuranceDetailSchema),
  });
  const insuranceModalProps = useInsurancePolicyModal(
    methods.control,
    initialPage,
  );

  const handleClose = () => {
    methods.reset(getDefaultValues());
    onClose();
    // navigate to iniital page
    if (initialPage) insuranceModalProps.setPage(initialPage);
    else insuranceModalProps.setPage({ type: 'INSURANCE' });
  };

  const handleSubmit = methods.handleSubmit((values) => {
    // When the risk_object input has value OTHER, it will take the value of the risk_object_text input instead
    const coverages = (values.coverages || []).map(
      ({ risk_object_id, ...other }) => ({
        risk_object_id: validate(risk_object_id || '')
          ? risk_object_id
          : undefined,
        ...other,
      }),
    );

    updateOrAddPolicy.mutate({
      insurancePolicy: {
        ...values,
        id: initialData?.id || v4(),
        type:
          initialData?.type ||
          (type === 'CURRENT-INSURANCE' ? 'ACTIVE' : 'INACTIVE'),
        coverages,
        due_date: values.due_date
          ? format(new Date(values.due_date), 'MM-dd')
          : undefined,
        start_date: values.start_date
          ? format(new Date(values.start_date), 'yyyy-MM-dd')
          : undefined,
        documents: values.documents || [],
      },
      partyId,
    });
    handleClose();
  });

  return (
    <>
      <RemoveAlertDialog
        onClose={closeAlert.onClose}
        isOpen={closeAlert.isOpen}
        onConfirm={handleClose}
        title={t('ARE-YOU-SURE')}
        description={t(
          'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_ARE-YOU-SURE',
        )}
        confirmButtonText={t('CLOSE')}
      />
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={methods.formState.isDirty ? closeAlert.onOpen : handleClose}
          size="5xl"
        >
          <ModalOverlay />
          <AddInsuranceModalContextProvider
            defaultIsOpenCoverage={defaultIsOpenCoverage}
            initialData={initialData}
            customRiskObjects={customRiskObjects}
          >
            <FormProvider {...methods}>
              <ModalContent h="3xl" maxH="90%">
                <ModalHeader>
                  {t(
                    `ADVISORY-REPORTS_CREATION-FLOW_${type}_MODAL_${mode}_TITLE`,
                  )}
                </ModalHeader>
                <ModalCloseButton />
                <AddInsuranceModalBody
                  type={type}
                  mode={mode}
                  insuranceModalProps={insuranceModalProps}
                  partyType={partyType}
                />
                <AddInsuranceModalFooter
                  onSubmit={handleSubmit}
                  onClose={
                    methods.formState.isDirty ? closeAlert.onOpen : handleClose
                  }
                  insuranceModalProps={insuranceModalProps}
                />
              </ModalContent>
            </FormProvider>
          </AddInsuranceModalContextProvider>
        </Modal>
      )}
    </>
  );
};

export default PartyAddInsurancePolicyModal;
